import React, { useRef, useState } from 'react';
import { JustForYou, ActionLink } from '@teko/teko-ecommerce';
import { useTenantConfig, useSearch } from '../hooks';
import { LIMIT_ITEM_PER_PAGE_PRODUCT_LIST } from 'constants/index';
import { ObjectMap } from 'types';
import { ProductCardProps } from '@teko/types/ecommerce';
import { SortEnum } from '@teko/discovery/v2';

const HEADER_HEIGHT = 56;

interface DiscoveryRecommendedProductsProps {
  paramsObject?: ObjectMap;
  title?: string;
  actionLink?: ActionLink;
  // need pass props with specific requirement: Ex: MML need 10 items / page
  itemsPerPage?: number;
  ssrProducts?: ProductCardProps[]; // for SSR
}

export const DiscoveryRecommendedProducts: React.FunctionComponent<
  DiscoveryRecommendedProductsProps
> = ({
  paramsObject,
  title,
  actionLink,
  itemsPerPage,
  ssrProducts = [],
}: DiscoveryRecommendedProductsProps) => {
  const { location, isMobile } = useTenantConfig();
  const [pageNumber, setPageNumber] = useState(1);
  const [productList, setProductList] = useState<ProductCardProps[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = itemsPerPage || LIMIT_ITEM_PER_PAGE_PRODUCT_LIST;

  const { isLoading, transformProduct } = useSearch(
    {
      ...paramsObject,
      page: pageNumber,
      location,
      pageSize: LIMIT_ITEM_PER_PAGE_PRODUCT_LIST,
      sorting: {
        sort: SortEnum.SORT_BY_TOP_SALE_QUANTITY_30_DAYS,
      },
    },
    {
      products: ssrProducts,
    },
    {
      /**
       * Mobile: append new products to the list
       * Desktop: replace the current products list
       * */
      onSuccess: (response) => {
        const { pagination, products } = response;
        setTotalItems(pagination?.totalItems as number);
        if (isMobile) {
          setProductList([...productList, ...(products || [])]);
        } else {
          setProductList(products || []);
        }
      },

      onError: () => {
        if (!isMobile) {
          setProductList([]);
        }
      },
    },
  );

  const top = useRef(null);

  const handlePageChanged = (page: number) => {
    if (!isMobile && top && top.current) {
      const element = top.current;
      window.scrollTo({
        left: 0,
        top: getElementOffset(element).top - HEADER_HEIGHT - 30,
      });
    }
    setPageNumber(page);
  };

  return (
    <div ref={top}>
      <JustForYou
        header={{
          title: title || '',
          link: (totalItems > pageSize && actionLink) || undefined,
          style: { paddingLeft: '16px' },
        }}
        products={productList?.map(transformProduct)}
        productsPerRow={isMobile ? 2 : 5}
        pagination={{
          totalRecords: totalItems,
          currentPage: pageNumber,
          pageLimit: pageSize,
          pageNeighbor: 2,
          onPageChange: handlePageChanged,
          color: 'primary500',
        }}
        isMobile={isMobile}
        isLoading={isLoading}
      />
    </div>
  );
};

//get element offset relative to the document
const getElementOffset = (elem: HTMLElement) => {
  const box = elem.getBoundingClientRect();
  const body = document.body;
  const docEl = document.documentElement;
  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;
  const clientTop = docEl.clientTop || body.clientTop || 0;
  const clientLeft = docEl.clientLeft || body.clientLeft || 0;
  const top = box.top + scrollTop - clientTop;
  const left = box.left + scrollLeft - clientLeft;
  return { top, left };
};
